/**
 * MYB-277: 초과근무 임포트 / 52시간 모니터링 API 클라이언트
 */
import { api } from './client';

export interface OvertimeThresholds {
  normal_max: number;
  warning_min: number;
  caution_min: number;
  violation_min: number;
}

export interface OvertimePreviewRow {
  row_index: number;
  employee_id: string;
  employee_name?: string;
  work_date: string;
  regular_hours: number;
  overtime_hours: number;
  night_hours: number;
  holiday_hours: number;
  _errors: string[];
}

export interface OvertimePreview {
  filename: string;
  columns: string[];
  total_rows: number;
  preview_rows: OvertimePreviewRow[];
  valid_count: number;
  error_count: number;
  errors: Array<{ row_index: number; errors: string[] }>;
}

export interface OvertimeImportResult {
  created: number;
  updated: number;
  skipped: number;
  error_count: number;
  errors: Array<{ row_index: number; employee_id: string; errors: string[] }>;
  total_rows: number;
  affected_employees: number;
}

export interface OvertimeWeeklyRow {
  id: number;
  employee_id: string;
  week_start: string;
  week_end: string;
  regular_hours: number;
  overtime_hours: number;
  night_hours: number;
  holiday_hours: number;
  total_hours: number;
  work_days: number;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
}

export interface OvertimeAlert {
  id: number;
  employee_id: string;
  violation_type: string;
  violation_date: string;
  week_start: string;
  actual_value: number;
  threshold: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  details: string;
}

export interface OvertimeMonthlyRow {
  employee_id: string;
  year_month: string;
  regular_hours: number;
  overtime_hours: number;
  night_hours: number;
  holiday_hours: number;
  total_hours: number;
  work_days: number;
  weekly_over_52_count: number;
}

export interface OvertimeDailyRecord {
  id: number;
  employee_id: string;
  work_date: string;
  regular_hours: number;
  overtime_hours: number;
  night_hours: number;
  holiday_hours: number;
  total_hours: number;
  work_type: string;
}

export const overtimeApi = {
  /** CSV 미리보기 (저장 없음) */
  async previewImport(file: File): Promise<{ success: boolean; data: OvertimePreview }> {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post('/api/v1/overtime/import/preview', form);
    return res.data;
  },

  /** CSV 일괄 임포트 */
  async importRecords(
    file: File,
    skipErrors = false,
  ): Promise<{ success: boolean; data: OvertimeImportResult }> {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post(
      `/api/v1/overtime/import?skip_errors=${skipErrors}`,
      form,
    );
    return res.data;
  },

  /** 주간 집계 목록 */
  async getWeeklySummary(params?: {
    week_start?: string;
    employee_id?: string;
  }): Promise<{ success: boolean; data: { rows: OvertimeWeeklyRow[]; total: number; thresholds: OvertimeThresholds } }> {
    const res = await api.get('/api/v1/overtime/weekly-summary', { params });
    return res.data;
  },

  /** 알림 목록 */
  async getAlerts(params?: {
    severity?: 'violation' | 'warning';
    week_start?: string;
    employee_id?: string;
  }): Promise<{
    success: boolean;
    data: {
      alerts: OvertimeAlert[];
      total: number;
      counts: { critical: number; high: number; medium: number };
      thresholds: OvertimeThresholds;
    };
  }> {
    const res = await api.get('/api/v1/overtime/alerts', { params });
    return res.data;
  },

  /** 월간 추이 */
  async getMonthlyTrend(params?: {
    employee_id?: string;
    months?: number;
  }): Promise<{ success: boolean; data: { rows: OvertimeMonthlyRow[]; total: number } }> {
    const res = await api.get('/api/v1/overtime/monthly-trend', { params });
    return res.data;
  },

  /** 일별 기록 */
  async getRecords(params?: {
    employee_id?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
  }): Promise<{ success: boolean; data: { records: OvertimeDailyRecord[]; total: number } }> {
    const res = await api.get('/api/v1/overtime/records', { params });
    return res.data;
  },
};
